import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { promotionsService } from '../services/api';

// Fonction utilitaire pour formater les dates
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const PromotionStats = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, [id]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await promotionsService.getStats(id);
      setStats(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des statistiques:', err);
      setError('Impossible de charger les statistiques');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!stats) {
    return null;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Accueil
        </Link>
        <Link component={RouterLink} to="/promotions" underline="hover" color="inherit">
          Promotions
        </Link>
        <Typography color="text.primary">Statistiques</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom>
        Statistiques - {stats.promotion.name}
      </Typography>

      {stats.promotion.code && (
        <Chip
          label={stats.promotion.code}
          sx={{ mb: 3, fontFamily: 'monospace', fontWeight: 'bold' }}
          color="primary"
        />
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Utilisations totales
              </Typography>
              <Typography variant="h3" color="primary">
                {stats.stats.totalUses}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Utilisateurs uniques
              </Typography>
              <Typography variant="h3" color="secondary">
                {stats.stats.uniqueUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Réduction totale accordée
              </Typography>
              <Typography variant="h3" color="error">
                {stats.stats.totalDiscount.toFixed(2)}€
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Historique des utilisations</Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Utilisateur ID</TableCell>
                <TableCell>Commande ID</TableCell>
                <TableCell align="right">Réduction</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stats.stats.usages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Aucune utilisation enregistrée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stats.stats.usages.map((usage) => (
                  <TableRow key={usage.id} hover>
                    <TableCell>{formatDate(usage.usedAt)}</TableCell>
                    <TableCell>
                      {usage.userId ? (
                        <Chip label={`User #${usage.userId}`} size="small" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Invité
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {usage.orderId ? (
                        <Chip
                          label={`Commande #${usage.orderId}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="medium" color="error.main">
                        -{usage.discountAmount.toFixed(2)}€
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default PromotionStats;

